import { useState, useEffect } from "react";
import { useFetchData, useUpdateData } from 'services/queries';
import { formatNcmCode } from 'utils/formatters/ncmFormatter';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import {
    GridToolbar,
    DataGridPremium
} from '@mui/x-data-grid-premium';
import avatar from 'assets/img/avatars/avatar_bewise.png';
import Grid from '@mui/material/Unstable_Grid2';
import NcmModalController from 'components/ncm/NcmModalController';

const NcmTaxes = () => {
    const [tableData, setTableData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentProductId, setCurrentProductId] = useState(null);
    const [currentNcmCode, setCurrentNcmCode] = useState('');
    const [currentNcmValues, setCurrentNcmValues] = useState(null);

    const updateProduct = useUpdateData('product');
    const { data: ncmTaxes, isLoading: isLoadingNcmTaxes, error: errorNcmTaxes, refetch: refetchNcmTaxes } = useFetchData('tax/ncm-tec', true);
    const { data: products, isLoading: isLoadingProducts, error: errorProducts, refetch: refetchProducts } = useFetchData('product', true);

    // merge tableData with ncmTaxes (ncm_tec.id = product.ncm_tec)
    useEffect(() => {
        if (ncmTaxes && products) {
            let mergedTableData = products.map((product) => {
                const ncmTax = ncmTaxes.find((ncmTax) => ncmTax.id === product.ncm_tec);
                return {
                    ...product,
                    ncm_code: ncmTax?.ncm_code,
                    ncm_description: ncmTax?.ncm_description,
                    ncm_external_code: product.ncm_external_code,
                    unit_measure: ncmTax?.unit_measure,
                    destaque: ncmTax?.destaque,
                    tipo_destaque: ncmTax?.tipo_destaque,
                    ii: ncmTax?.ii,
                    tipo_ii: ncmTax?.tipo_ii,
                    ipi: ncmTax?.ipi,
                    tipo_ipi: ncmTax?.tipo_ipi,
                    pis_pasep: ncmTax?.pis_pasep,
                    cofins: ncmTax?.cofins,
                    tipo_icms: ncmTax?.tipo_icms,
                    reducao_base_pis_cofins: product.reducao_base_pis_cofins,
                    majoracao_cofins: product.majoracao_cofins != null
                        ? product.majoracao_cofins
                        : ncmTax?.majoracao_cofins,
                };
            });
            setTableData(() => mergedTableData);
        }
    }, [ncmTaxes, products, updateProduct.isSuccess]);

    const openNcmModal = (productId, currentNcmCode) => {
        setCurrentProductId(productId);
        setCurrentNcmCode(currentNcmCode);

        // Always get fresh data from tableData to ensure we have the latest values
        const product = tableData.find(p => p.id === productId);
        if (product && currentNcmCode) {
            const currentValues = {
                ncm_code: currentNcmCode,
                ncm_external_code: product.ncm_external_code,
                ncm_description: product.ncm_description,
                reducao_base_pis_cofins: product.reducao_base_pis_cofins,
                majoracao_cofins: product.majoracao_cofins,
            };
            setCurrentNcmValues(currentValues);
        } else {
            setCurrentNcmValues(null);
        }

        setIsModalOpen(true);
    };

    const handleSaveNcm = async (productId, formData) => {
        if (!productId || !formData.ncm_code) return;

        try {
            const product = tableData.find(p => p.id === productId);
            const updatedProduct = {
                ncm_external_code: formData.ncm_external_code,
                ncm_code: formData.ncm_code,
                name: product?.name || '',
                reducao_base_pis_cofins: formData.reducao_base_pis_cofins,
                majoracao_cofins: formData.majoracao_cofins,
            };

            await updateProduct.mutateAsync({ id: productId, newData: updatedProduct });

            // majoracao_cofins is now per-product on Product. The previous PATCH to
            // /api/tax/ncm-tec/ was a shared-state write affecting every product with
            // the same NCM; that path is retired in favor of Product.majoracao_cofins.

            refetchNcmTaxes();
            refetchProducts();
        } catch (error) {
            console.error("Error saving NCM data:", error);
            throw error;
        }
    };


    const columns = [
        {
            field: 'name',
            headerName: 'Product',
            groupable: false,
            aggregable: false,
            minWidth: 350,
            renderCell: (params) => {
              if (params.rowNode.type === 'group') {
                return params.value;
              }
              const product = params.row;
              const name = product?.name || '';
              const picture = product?.picture || avatar;
              const sku = product?.sku || '';

              if (product) {
                return (
                  <Grid container alignItems="center" pr={2} style={{ display: 'flex', flexDirection: 'row' }}>
                    <Grid
                      xs={2}
                      mx={0.5} marginRight={1}
                      maxWidth={45}
                    >
                      {
                        picture ? (
                          <img src={picture} className="w-10 h-10 object-contain drop-shadow-md" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100"></div>
                        )
                      }
                    </Grid>
                    <Grid flex={1}
                      sx={{ lineHeight: 'normal' }} py={0.5}>
                      <Typography
                        variant="subtitle2"
                        style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {name}
                      </Typography>
                      <Typography variant="caption">{sku}</Typography>
                    </Grid>
                    </Grid>
                );
              }
              return '';
            }
        },
        {
            field: 'ncm_code',
            headerName: 'NCM Code',
            type: 'text',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 150,
            visibleTo: ['Administrator', 'Staff'],
            valueFormatter: (value) => formatNcmCode(value),
            renderCell: (params) => {
                const hasNcmCode = Boolean(params.value);

                return (
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Tooltip title={hasNcmCode ? "Edit NCM code" : "Assign NCM code to this product"}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => openNcmModal(params.row.id, params.value)}
                                sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    position: 'relative',
                                    minWidth: '100px',
                                    paddingLeft: 2,
                                    paddingRight: 2,
                                    borderColor: hasNcmCode ? 'primary.main' : 'warning.light',
                                    color: hasNcmCode ? 'primary.main' : 'warning.dark',
                                    backgroundColor: hasNcmCode ? 'transparent' : 'rgba(255, 152, 0, 0.05)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: hasNcmCode ? 'primary.dark' : 'warning.main',
                                        backgroundColor: hasNcmCode ? 'action.hover' : 'rgba(255, 152, 0, 0.1)',
                                        '& .action-icon': {
                                            opacity: 1,
                                            transform: 'translateX(0)'
                                        }
                                    }
                                }}
                                endIcon={
                                    <EditIcon
                                        fontSize="small"
                                        className="action-icon"
                                        sx={{
                                            opacity: hasNcmCode ? 0.4 : 0.7,
                                            transform: 'translateX(-3px)',
                                            transition: 'all 0.2s',
                                            mr: -0.5
                                        }}
                                    />
                                }
                            >
                                {hasNcmCode ? formatNcmCode(params.value) : 'Assign'}
                            </Button>
                        </Tooltip>
                    </Box>
                )
            },
        },
        {
            field: 'ncm_description',
            headerName: 'NCM Description',
            type: 'text',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 350,
            flex: 1,
            visibleTo: ['Administrator', 'Staff'],
        },
        {
            field: 'unit_measure',
            headerName: 'Unit Measure',
            type: 'text',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 100,
            visibleTo: ['Administrator', 'Staff'],
            valueGetter: (_, row) => {
                return row?.unit_measure?.description;
            }
        },
        {
            field: 'ii',
            headerName: 'II',
            type: 'number',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 100,
            visibleTo: ['Administrator', 'Staff'],
            valueFormatter: (value) => {
                if (value == null) return '';
                return `${(value * 100).toFixed(2)}%`;
            }
        },
        {
            field: 'ipi',
            headerName: 'IPI',
            type: 'number',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 100,
            visibleTo: ['Administrator', 'Staff'],
            valueFormatter: (value) => {
                if (value == null) return '';
                return `${(value * 100).toFixed(2)}%`;
            }
        },
        {
            field: 'pis_pasep',
            headerName: 'PIS/PASEP',
            type: 'number',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 100,
            visibleTo: ['Administrator', 'Staff'],
            valueFormatter: (value) => {
                if (value == null) return '';
                return `${(value * 100).toFixed(2)}%`;
            }
        },
        {
            field: 'cofins',
            headerName: 'COFINS',
            type: 'number',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 100,
            visibleTo: ['Administrator', 'Staff'],
            valueFormatter: (value) => {
                if (value == null) return '';
                return `${(value * 100).toFixed(2)}%`;
            }
        },
        {
            field: 'reducao_base_pis_cofins',
            headerName: 'Redução PIS/COFINS',
            type: 'number',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 150,
            visibleTo: ['Administrator', 'Staff'],
            valueFormatter: (value) => {
                if (value == null) return '';
                // Value is already in decimal format (0.25 for 25%)
                return `${(parseFloat(value) * 100).toFixed(2)}%`;
            }
        },
        {
            field: 'majoracao_cofins',
            headerName: 'Majoração COFINS',
            type: 'number',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 150,
            visibleTo: ['Administrator', 'Staff'],
            valueFormatter: (value) => {
                if (value == null) return '';
                return `${(parseFloat(value) * 100).toFixed(2)}%`;
            }
        },
        {
            field: 'tipo_icms',
            headerName: 'Tipo ICMS',
            type: 'text',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 100,
            visibleTo: ['Administrator', 'Staff'],
        },
        {
            field: 'destaque',
            headerName: 'Destaque',
            type: 'number',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 100,
            visibleTo: ['Administrator', 'Staff'],
        },
        {
            field: 'tipo_destaque',
            headerName: 'Tipo Destaque',
            type: 'text',
            editable: false,
            groupable: false,
            aggregable: false,
            minWidth: 120,
            visibleTo: ['Administrator', 'Staff'],
        },
    ];

    if (isLoadingNcmTaxes || isLoadingProducts) {
        return (
            <div className='h-100'>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <Typography variant="body1" align="center">
                        Loading...
                    </Typography>
                </div>
            </div>
        )
    }

    if (errorNcmTaxes || errorProducts) {
        return (
            <div className='h-100'>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <Typography variant="body1" align="center">
                        Error fetching data. Please try again.
                    </Typography>
                </div>
            </div>
        )
    }

    return (
        <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
            <div className='flex justify-between items-center'>
                <h3 className="pt-[5px] text-2xl font-bold text-navy-700 dark:text-white">NCM Taxes</h3>
            </div>
            <Box>
                <DataGridPremium
                    columns={columns}
                    rows={tableData}
                    autosizeOnMount={true}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                    pagination
                    pageSizeOptions={[10, 25, 50, 100]}
                />

                <NcmModalController
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        // Clear modal state when closing
                        setCurrentProductId(null);
                        setCurrentNcmCode('');
                        setCurrentNcmValues(null);
                    }}
                    onSave={handleSaveNcm}
                    initialProductId={currentProductId}
                    initialNcmCode={currentNcmCode}
                    initialNcmValues={currentNcmValues}
                />
            </Box>
        </div>
    );
};

export default NcmTaxes;